import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import Card from '../../components/base/Card';

interface Listing {
  id: number;
  name: string;
  location: string;
  category: string;
  price: number;
  rating: number;
  reviews: number;
  image: string;
  features: string[];
}

export default function AdvertisePage() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedState, setSelectedState] = useState('');
  const [selectedPrice, setSelectedPrice] = useState('');
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showStateDropdown, setShowStateDropdown] = useState(false);
  const [showPriceDropdown, setShowPriceDropdown] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [showReservationModal, setShowReservationModal] = useState(false);
  const [reservationListing, setReservationListing] = useState<any>(null);
  const [listings, setListings] = useState<Listing[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const categories = [
    'Todas las categorías',
    'Fútbol 11',
    'Fútbol 7',
    'Fútbol 5',
    'Futsal',
    'Fútbol Playa'
  ];

  const states = [
    'Todos los estados',
    'Ciudad de México',
    'Guadalajara',
    'Monterrey',
    'Puebla',
    'Tijuana',
    'León',
    'Juárez',
    'Torreón'
  ];

  const priceRanges = [
    'Todos los precios',
    '$100 - $300',
    '$300 - $500',
    '$500 - $800',
    '$800 - $1200',
    'Más de $1200'
  ];

  useEffect(() => {
    fetchListings();
  }, []);

  const fetchListings = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // 🔧 MODIFICAR: Reemplaza con tu endpoint real cuando esté listo
      // Por ahora, simularemos que la API no está disponible y usaremos datos de ejemplo
      const response = await fetch('/api/listings/all');
      
      if (!response.ok) {
        // Si la API no responde, usar datos de ejemplo
        throw new Error('API no disponible');
      }
      
      const data = await response.json();
      setListings(data);
      setError(null);
    } catch (err) {
      console.log('Usando datos de ejemplo (API no conectada)');
      // No mostrar error, simplemente usar datos de ejemplo
      setError(null);
      
      // Datos de ejemplo como fallback
      setListings([
        {
          id: 1,
          name: 'Cancha Los Campeones',
          location: 'Polanco, Ciudad de México',
          category: 'Fútbol 11',
          price: 800,
          rating: 4.8,
          reviews: 156,
          image: 'https://readdy.ai/api/search-image?query=professional%20soccer%20field%20with%20green%20artificial%20turf%2C%20stadium%20lights%2C%20goal%20posts%2C%20modern%20sports%20facility%2C%20well%20maintained%20pitch%2C%20bright%20lighting%2C%20realistic%20sports%20venue&width=400&height=250&seq=listing1&orientation=landscape',
          features: ['Césped sintético', 'Iluminación LED', 'Estacionamiento', 'Vestidores']
        },
        {
          id: 2,
          name: 'Deportivo San Rafael',
          location: 'San Rafael, Ciudad de México',
          category: 'Fútbol 7',
          price: 450,
          rating: 4.6,
          reviews: 89,
          image: 'https://readdy.ai/api/search-image?query=soccer%20field%20number%207%20artificial%20grass%2C%20smaller%20football%20pitch%2C%20goal%20posts%2C%20modern%20sports%20center%2C%20clean%20facilities%2C%20professional%20lighting%2C%20green%20turf&width=400&height=250&seq=listing2&orientation=landscape',
          features: ['Césped natural', 'Gradas', 'Cancha techada', 'Seguridad']
        },
        {
          id: 3,
          name: 'Futsal Arena Pro',
          location: 'Roma Norte, Ciudad de México',
          category: 'Futsal',
          price: 350,
          rating: 4.9,
          reviews: 203,
          image: 'https://readdy.ai/api/search-image?query=indoor%20futsal%20court%20with%20wooden%20floor%2C%20professional%20indoor%20soccer%20facility%2C%20bright%20lighting%2C%20modern%20sports%20hall%2C%20clean%20environment%2C%20goals%20and%20markings&width=400&height=250&seq=listing3&orientation=landscape',
          features: ['Piso de madera', 'Aire acondicionado', 'Sonido', 'Cafetería']
        },
        {
          id: 4,
          name: 'Complejo Deportivo Azteca',
          location: 'Coyoacán, Ciudad de México',
          category: 'Fútbol 5',
          price: 280,
          rating: 4.4,
          reviews: 67,
          image: 'https://readdy.ai/api/search-image?query=small%20soccer%20field%20for%205%20players%2C%20artificial%20turf%2C%20enclosed%20court%20with%20walls%2C%20modern%20sports%20facility%2C%20good%20lighting%2C%20professional%20appearance&width=400&height=250&seq=listing4&orientation=landscape',
          features: ['Canchas múltiples', 'Torneos regulares', 'Alquiler equipos', 'Tienda deportiva']
        },
        {
          id: 5,
          name: 'Green Field Premium',
          location: 'Santa Fe, Ciudad de México',
          category: 'Fútbol 11',
          price: 1200,
          rating: 4.7,
          reviews: 124,
          image: 'https://readdy.ai/api/search-image?query=premium%20soccer%20field%20with%20perfect%20green%20artificial%20grass%2C%20professional%20stadium%20quality%2C%20excellent%20lighting%2C%20modern%20sports%20complex%2C%20high-end%20facility&width=400&height=250&seq=listing5&orientation=landscape',
          features: ['Césped premium', 'VAR disponible', 'Transmisión live', 'Servicio médico']
        },
        {
          id: 6,
          name: 'Cancha Barrio Alegre',
          location: 'Iztapalapa, Ciudad de México',
          category: 'Fútbol 7',
          price: 220,
          rating: 4.2,
          reviews: 45,
          image: 'https://readdy.ai/api/search-image?query=neighborhood%20soccer%20field%2C%20community%20sports%20facility%2C%20artificial%20turf%2C%20basic%20but%20well%20maintained%2C%20local%20football%20pitch%2C%20accessible%20sports%20venue&width=400&height=250&seq=listing6&orientation=landscape',
          features: ['Precios accesibles', 'Ambiente familiar', 'Ligas locales', 'Fácil acceso']
        },
        {
          id: 7,
          name: 'Estadio Municipal Norte',
          location: 'Naucalpan, Estado de México',
          category: 'Fútbol 11',
          price: 950,
          rating: 4.6,
          reviews: 98,
          image: 'https://readdy.ai/api/search-image?query=municipal%20soccer%20stadium%20with%20professional%20grass%2C%20large%20football%20field%20with%20bleachers%2C%20well%20maintained%20pitch%2C%20municipal%20sports%20facility%2C%20green%20natural%20turf&width=400&height=250&seq=listing7&orientation=landscape',
          features: ['Césped natural', 'Tribunas grandes', 'Camerinos VIP', 'Sistema de riego']
        },
        {
          id: 8,
          name: 'Arena Indoor Sport',
          location: 'Satelite, Estado de México',
          category: 'Futsal',
          price: 420,
          rating: 4.8,
          reviews: 167,
          image: 'https://readdy.ai/api/search-image?query=modern%20indoor%20futsal%20arena%2C%20professional%20indoor%20soccer%20facility%2C%20excellent%20lighting%2C%20climate%20controlled%20sports%20hall%2C%20wooden%20floor%20court&width=400&height=250&seq=listing8&orientation=landscape',
          features: ['Climatizado', 'Piso profesional', 'Gradas techadas', 'Bar deportivo']
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    console.log('Búsqueda:', { searchTerm, selectedCategory, selectedState, selectedPrice });
  };

  const handleViewDetails = (listing: any) => {
    navigate(`/anuncio/${listing.id}`);
  };

  const handleReserveNow = (listing: any) => {
    setReservationListing(listing);
    setShowReservationModal(true);
  };

  const handleCloseReservation = () => {
    setShowReservationModal(false);
    setReservationListing(null);
  };

  const filteredListings = listings.filter(listing => {
    const matchesSearch =
      searchTerm === '' ||
      listing.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      listing.location.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesCategory =
      selectedCategory === '' ||
      selectedCategory === 'Todas las categorías' ||
      listing.category === selectedCategory;

    const matchesState =
      selectedState === '' ||
      selectedState === 'Todos los estados' ||
      listing.location.includes(selectedState);

    return matchesSearch && matchesCategory && matchesState;
  });

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative py-16 bg-gradient-to-r from-green-600 to-green-700">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Encuentra tu Cancha Perfecta
            </h1>
            <p className="text-xl md:text-2xl opacity-90 leading-relaxed mb-8">
              Descubre las mejores canchas de fútbol cerca de ti
            </p>
          </div>
        </div>
      </section>

      {/* Main Content - Two Columns */}
      <section className="py-8">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            {error && (
              <div className="bg-yellow-50 border-2 border-yellow-400 text-yellow-800 px-4 py-3 rounded-lg mb-6">
                <p className="text-sm">{error}</p>
              </div>
            )}

            {loading ? (
              <div className="text-center py-20">
                <div className="inline-block w-12 h-12 border-4 border-green-600 border-t-transparent rounded-full animate-spin"></div>
                <p className="mt-4 text-gray-600">Cargando canchas...</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
                {/* Left Column - Search and Filters */}
                <div className="lg:col-span-1">
                  <div className="bg-white rounded-lg shadow-md p-6 sticky top-24">
                    <h3 className="text-xl font-bold text-gray-800 mb-6">Filtros de Búsqueda</h3>

                    {/* Search Bar */}
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Buscar</label>
                      <div className="relative">
                        <input
                          type="text"
                          placeholder="Nombre o ubicación..."
                          value={searchTerm}
                          onChange={e => setSearchTerm(e.target.value)}
                          className="w-full px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                        />
                        <i className="ri-search-line absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                      </div>
                    </div>

                    {/* Category Filter */}
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Categoría</label>
                      <div className="relative">
                        <button
                          onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                          className="w-full px-4 py-3 text-left border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white cursor-pointer text-sm flex items-center justify-between"
                        >
                          <span className={selectedCategory || 'text-gray-500'}>
                            {selectedCategory || 'Seleccionar categoría'}
                          </span>
                          <i className={`ri-arrow-down-s-line transition-transform ${showCategoryDropdown ? 'rotate-180' : ''}`}></i>
                        </button>

                        {showCategoryDropdown && (
                          <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-10 max-h-60 overflow-y-auto">
                            {categories.map(category => (
                              <button
                                key={category}
                                onClick={() => {
                                  setSelectedCategory(category);
                                  setShowCategoryDropdown(false);
                                }}
                                className="w-full px-4 py-2 text-left hover:bg-gray-50 text-sm cursor-pointer"
                              >
                                {category}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Location Filter */}
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Ubicación</label>
                      <div className="relative">
                        <button
                          onClick={() => setShowStateDropdown(!showStateDropdown)}
                          className="w-full px-4 py-3 text-left border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white cursor-pointer text-sm flex items-center justify-between"
                        >
                          <span className={selectedState || 'text-gray-500'}>
                            {selectedState || 'Seleccionar ubicación'}
                          </span>
                          <i className={`ri-arrow-down-s-line transition-transform ${showStateDropdown ? 'rotate-180' : ''}`}></i>
                        </button>

                        {showStateDropdown && (
                          <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-10 max-h-60 overflow-y-auto">
                            {states.map(state => (
                              <button
                                key={state}
                                onClick={() => {
                                  setSelectedState(state);
                                  setShowStateDropdown(false);
                                }}
                                className="w-full px-4 py-2 text-left hover:bg-gray-50 text-sm cursor-pointer"
                              >
                                {state}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Price Filter */}
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Rango de Precio</label>
                      <div className="relative">
                        <button
                          onClick={() => setShowPriceDropdown(!showPriceDropdown)}
                          className="w-full px-4 py-3 text-left border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white cursor-pointer text-sm flex items-center justify-between"
                        >
                          <span className={selectedPrice || 'text-gray-500'}>
                            {selectedPrice || 'Seleccionar precio'}
                          </span>
                          <i className={`ri-arrow-down-s-line transition-transform ${showPriceDropdown ? 'rotate-180' : ''}`}></i>
                        </button>

                        {showPriceDropdown && (
                          <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-10 max-h-60 overflow-y-auto">
                            {priceRanges.map(range => (
                              <button
                                key={range}
                                onClick={() => {
                                  setSelectedPrice(range);
                                  setShowPriceDropdown(false);
                                }}
                                className="w-full px-4 py-2 text-left hover:bg-gray-50 text-sm cursor-pointer"
                              >
                                {range}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    <Button onClick={handleSearch} className="w-full mb-4">
                      <i className="ri-search-line mr-2"></i>
                      Aplicar Filtros
                    </Button>

                    {/* Clear Filters */}
                    <button
                      onClick={() => {
                        setSearchTerm('');
                        setSelectedCategory('');
                        setSelectedState('');
                        setSelectedPrice('');
                      }}
                      className="w-full text-sm text-gray-600 hover:text-gray-800 cursor-pointer"
                    >
                      Limpiar filtros
                    </button>
                  </div>
                </div>

                {/* Right Column - Results */}
                <div className="lg:col-span-3">
                  {/* Results Header */}
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold text-gray-800">
                      {filteredListings.length} canchas encontradas
                    </h2>
                    <div className="flex items-center space-x-4">
                      {/* View Mode Toggle */}
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => setViewMode('grid')}
                          className={`w-10 h-10 flex items-center justify-center rounded ${viewMode === 'grid' ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-600'} cursor-pointer`}
                        >
                          <i className="ri-grid-line"></i>
                        </button>
                        <button
                          onClick={() => setViewMode('list')}
                          className={`w-10 h-10 flex items-center justify-center rounded ${viewMode === 'list' ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-600'} cursor-pointer`}
                        >
                          <i className="ri-list-check"></i>
                        </button>
                      </div>

                      {/* Sort */}
                      <div className="flex items-center space-x-2">
                        <span className="text-gray-600 text-sm">Ordenar:</span>
                        <select className="border border-gray-300 rounded px-3 py-2 text-sm pr-8">
                          <option>Más relevantes</option>
                          <option>Precio menor</option>
                          <option>Precio mayor</option>
                          <option>Mejor calificados</option>
                          <option>Más cercanos</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Listings Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredListings.map(listing => (
                      <Card
                        key={listing.id}
                        className={`overflow-hidden hover:shadow-lg transition-shadow bg-white rounded-lg border-2 border-red-200 ${viewMode === 'list' ? 'flex' : ''}`}
                      >
                        <div className={`${viewMode === 'list' ? 'flex' : ''}`}>
                          <div className={`${viewMode === 'list' ? 'w-80 flex-shrink-0' : ''}`}>
                            <img
                              src={listing.image}
                              alt={listing.name}
                              className={`w-full object-cover object-top ${viewMode === 'list' ? 'h-48' : 'h-48'}`}
                            />
                          </div>
                          <div className={`p-6 ${viewMode === 'list' ? 'flex-1' : ''}`}>
                            <div className="flex justify-between items-start mb-2">
                              <h3 className="text-xl font-bold text-gray-800">{listing.name}</h3>
                              <span className="text-green-600 font-bold text-lg">${listing.price}/hora</span>
                            </div>

                            <div className="flex items-center text-gray-600 mb-2">
                              <i className="ri-map-pin-line mr-1"></i>
                              <span className="text-sm">{listing.location}</span>
                            </div>

                            <div className="flex items-center mb-3">
                              <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium">
                                {listing.category}
                              </span>
                              <div className="ml-auto flex items-center">
                                <i className="ri-star-fill text-yellow-500 mr-1"></i>
                                <span className="font-medium">{listing.rating}</span>
                                <span className="text-gray-500 text-sm ml-1">({listing.reviews})</span>
                              </div>
                            </div>

                            <div className={`grid ${viewMode === 'list' ? 'grid-cols-2' : 'grid-cols-2'} gap-2 mb-4`}>
                              {listing.features.map((feature, index) => (
                                <div key={index} className="flex items-center text-sm text-gray-600">
                                  <i className="ri-check-line text-green-600 mr-1"></i>
                                  <span className="truncate">{feature}</span>
                                </div>
                              ))}
                            </div>

                            {/* Action Buttons */}
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleViewDetails(listing)}
                                className="font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center border-2 border-green-600 text-green-600 hover:bg-green-600 hover:text-white px-3 py-1.5 text-sm flex-1"
                              >
                                Ver Detalles
                              </button>
                              <button
                                onClick={() => handleReserveNow(listing)}
                                className="font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center bg-green-600 text-white hover:bg-green-700 px-3 py-1.5 text-sm flex-1"
                              >
                                Reservar Ahora
                              </button>
                            </div>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>

                  {/* Pagination */}
                  <div className="flex justify-center mt-12">
                    <div className="flex items-center space-x-2">
                      <button className="px-3 py-2 border border-gray-300 rounded hover:bg-gray-50 cursor-pointer">
                        <i className="ri-arrow-left-line"></i>
                      </button>
                      <button className="px-3 py-2 bg-green-600 text-white rounded cursor-pointer">1</button>
                      <button className="px-3 py-2 border border-gray-300 rounded hover:bg-gray-50 cursor-pointer">2</button>
                      <button className="px-3 py-2 border border-gray-300 rounded hover:bg-gray-50 cursor-pointer">3</button>
                      <span className="px-3 py-2">...</span>
                      <button className="px-3 py-2 border border-gray-300 rounded hover:bg-gray-50 cursor-pointer">10</button>
                      <button className="px-3 py-2 border border-gray-300 rounded hover:bg-gray-50 cursor-pointer">
                        <i className="ri-arrow-right-line"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Reservation Modal */}
      {showReservationModal && reservationListing && (
        <ReservationModal
          listing={reservationListing}
          onClose={handleCloseReservation}
        />
      )}

      <Footer />
    </div>
  );
}

// Reservation Modal Component
const ReservationModal: React.FC<{ listing: any; onClose: () => void }> = ({ listing, onClose }) => {
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    telefono: '',
    fecha: '',
    horaInicio: '',
    horaFin: '',
    duracion: '1',
    numeroPersonas: '',
    tipoReserva: 'individual',
    serviciosAdicionales: [] as string[],
    comentarios: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const serviciosDisponibles = [
    { id: 'balon', label: 'Alquiler de Balón', precio: 50 },
    { id: 'chalecos', label: 'Chalecos de Entrenamiento', precio: 30 },
    { id: 'arbitro', label: 'Árbitro Profesional', precio: 200 },
    { id: 'agua', label: 'Agua Embotellada', precio: 20 },
    { id: 'seguro', label: 'Seguro de Accidentes', precio: 100 }
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleServiceToggle = (serviceId: string) => {
    setFormData(prev => ({
      ...prev,
      serviciosAdicionales: prev.serviciosAdicionales.includes(serviceId)
        ? prev.serviciosAdicionales.filter(id => id !== serviceId)
        : [...prev.serviciosAdicionales, serviceId]
    }));
  };

  const calculateTotal = () => {
    const duracionHoras = parseInt(formData.duracion) || 1;
    const precioBase = listing.price * duracionHoras;
    const precioServicios = formData.serviciosAdicionales.reduce((total, serviceId) => {
      const servicio = serviciosDisponibles.find(s => s.id === serviceId);
      return total + (servicio?.precio || 0);
    }, 0);
    return precioBase + precioServicios;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validación
    if (!formData.nombre || !formData.email || !formData.telefono || !formData.fecha || !formData.horaInicio || !formData.horaFin) {
      alert('Por favor completa todos los campos obligatorios');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formElement = e.target as HTMLFormElement;
      const formDataToSend = new FormData(formElement);

      const response = await fetch('https://readdy.ai/api/form/d3tehc955ieogu7si8l0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formDataToSend as any).toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error al enviar reserva:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto border-2 border-red-200">
        {/* Header */}
        <div className="sticky top-0 bg-green-600 text-white p-6 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
              <i className="ri-calendar-check-line text-2xl"></i>
            </div>
            <div>
              <h2 className="text-2xl font-bold">Reservar Cancha</h2>
              <p className="text-green-100 text-sm">{listing.name}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center hover:bg-white/20 rounded-full transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        {/* Form */}
        <form id="reserva-form" data-readdy-form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Información de la Cancha */}
          <div className="bg-gray-50 p-4 rounded-lg border-2 border-red-100">
            <div className="flex items-start gap-4">
              <img
                src={listing.image}
                alt={listing.name}
                className="w-24 h-24 object-cover rounded-lg"
              />
              <div className="flex-1">
                <h3 className="font-bold text-lg text-gray-900">{listing.name}</h3>
                <p className="text-gray-600 text-sm flex items-center gap-1 mt-1">
                  <i className="ri-map-pin-line"></i>
                  {listing.location}
                </p>
                <p className="text-green-600 font-bold text-xl mt-2">${listing.price} MXN/hora</p>
              </div>
            </div>
          </div>

          {/* Hidden fields for form submission */}
          <input type="hidden" name="cancha" value={listing.name} />
          <input type="hidden" name="ubicacion" value={listing.location} />
          <input type="hidden" name="precioHora" value={listing.price} />
          <input type="hidden" name="total" value={calculateTotal()} />

          {/* Información Personal */}
          <div className="space-y-4">
            <h3 className="font-bold text-lg text-gray-900 flex items-center gap-2">
              <i className="ri-user-line text-green-600"></i>
              Información Personal
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre Completo *
                </label>
                <input
                  type="text"
                  name="nombre"
                  value={formData.nombre}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-green-600 focus:outline-none text-sm"
                  placeholder="Juan Pérez García"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-green-600 focus:outline-none text-sm"
                  placeholder="correo@ejemplo.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Teléfono *
                </label>
                <input
                  type="tel"
                  name="telefono"
                  value={formData.telefono}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-green-600 focus:outline-none text-sm"
                  placeholder="55 1234 5678"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Número de Personas
                </label>
                <input
                  type="number"
                  name="numeroPersonas"
                  value={formData.numeroPersonas}
                  onChange={handleInputChange}
                  min="1"
                  max="22"
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-green-600 focus:outline-none text-sm"
                  placeholder="10"
                />
              </div>
            </div>
          </div>

          {/* Fecha y Horario */}
          <div className="space-y-4">
            <h3 className="font-bold text-lg text-gray-900 flex items-center gap-2">
              <i className="ri-calendar-line text-green-600"></i>
              Fecha y Horario
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Fecha de Reserva *
                </label>
                <input
                  type="date"
                  name="fecha"
                  value={formData.fecha}
                  onChange={handleInputChange}
                  required
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-green-600 focus:outline-none text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Duración (horas)
                </label>
                <select
                  name="duracion"
                  value={formData.duracion}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 pr-8 border-2 border-gray-300 rounded-lg focus:border-green-600 focus:outline-none text-sm"
                >
                  <option value="1">1 hora</option>
                  <option value="2">2 horas</option>
                  <option value="3">3 horas</option>
                  <option value="4">4 horas</option>
                  <option value="5">5 horas</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Hora de Inicio *
                </label>
                <input
                  type="time"
                  name="horaInicio"
                  value={formData.horaInicio}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-green-600 focus:outline-none text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Hora de Fin *
                </label>
                <input
                  type="time"
                  name="horaFin"
                  value={formData.horaFin}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-green-600 focus:outline-none text-sm"
                />
              </div>
            </div>
          </div>

          {/* Tipo de Reserva */}
          <div className="space-y-4">
            <h3 className="font-bold text-lg text-gray-900 flex items-center gap-2">
              <i className="ri-team-line text-green-600"></i>
              Tipo de Reserva
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <label className="flex items-center gap-3 p-4 border-2 border-gray-300 rounded-lg cursor-pointer hover:border-green-600 transition-colors">
                <input
                  type="radio"
                  name="tipoReserva"
                  value="individual"
                  checked={formData.tipoReserva === 'individual'}
                  onChange={handleInputChange}
                  className="w-5 h-5 text-green-600"
                />
                <div>
                  <div className="font-medium text-gray-900">Individual</div>
                  <div className="text-xs text-gray-500">Uso personal</div>
                </div>
              </label>

              <label className="flex items-center gap-3 p-4 border-2 border-gray-300 rounded-lg cursor-pointer hover:border-green-600 transition-colors">
                <input
                  type="radio"
                  name="tipoReserva"
                  value="equipo"
                  checked={formData.tipoReserva === 'equipo'}
                  onChange={handleInputChange}
                  className="w-5 h-5 text-green-600"
                />
                <div>
                  <div className="font-medium text-gray-900">Equipo</div>
                  <div className="text-xs text-gray-500">Partido amistoso</div>
                </div>
              </label>

              <label className="flex items-center gap-3 p-4 border-2 border-gray-300 rounded-lg cursor-pointer hover:border-green-600 transition-colors">
                <input
                  type="radio"
                  name="tipoReserva"
                  value="torneo"
                  checked={formData.tipoReserva === 'torneo'}
                  onChange={handleInputChange}
                  className="w-5 h-5 text-green-600"
                />
                <div>
                  <div className="font-medium text-gray-900">Torneo</div>
                  <div className="text-xs text-gray-500">Evento oficial</div>
                </div>
              </label>
            </div>
          </div>

          {/* Servicios Adicionales */}
          <div className="space-y-4">
            <h3 className="font-bold text-lg text-gray-900 flex items-center gap-2">
              <i className="ri-service-line text-green-600"></i>
              Servicios Adicionales
            </h3>

            <div className="space-y-2">
              {serviciosDisponibles.map(servicio => (
                <label
                  key={servicio.id}
                  className="flex items-center justify-between p-3 border-2 border-gray-300 rounded-lg cursor-pointer hover:border-green-600 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={formData.serviciosAdicionales.includes(servicio.id)}
                      onChange={() => handleServiceToggle(servicio.id)}
                      className="w-5 h-5 text-green-600 rounded"
                    />
                    <span className="font-medium text-gray-900">{servicio.label}</span>
                  </div>
                  <span className="text-green-600 font-bold">+${servicio.precio} MXN</span>
                </label>
              ))}
              <input
                type="hidden"
                name="serviciosAdicionales"
                value={formData.serviciosAdicionales.map(id =>
                  serviciosDisponibles.find(s => s.id === id)?.label
                ).join(', ')}
              />
            </div>
          </div>

          {/* Comentarios */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Comentarios o Solicitudes Especiales
            </label>
            <textarea
              name="comentarios"
              value={formData.comentarios}
              onChange={handleInputChange}
              maxLength={500}
              rows={4}
              className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-green-600 focus:outline-none text-sm resize-none"
              placeholder="Escribe aquí cualquier solicitud especial o comentario..."
            />
            <p className="text-xs text-gray-500 text-right">{formData.comentarios.length}/500 caracteres</p>
          </div>

          {/* Resumen de Pago */}
          <div className="bg-green-50 p-4 rounded-lg border-2 border-green-200">
            <h3 className="font-bold text-lg text-gray-900 mb-3">Resumen de Pago</h3>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Precio por hora:</span>
                <span className="font-medium">${listing.price} MXN</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Duración:</span>
                <span className="font-medium">{formData.duracion} {formData.duracion === '1' ? 'hora' : 'horas'}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Subtotal cancha:</span>
                <span className="font-medium">${listing.price * parseInt(formData.duracion)} MXN</span>
              </div>
              {formData.serviciosAdicionales.length > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Servicios adicionales:</span>
                  <span className="font-medium">
                    ${formData.serviciosAdicionales.reduce((total, serviceId) => {
                      const servicio = serviciosDisponibles.find(s => s.id === serviceId);
                      return total + (servicio?.precio || 0);
                    }, 0)} MXN
                  </span>
                </div>
              )}
              <div className="border-t-2 border-green-300 pt-2 mt-2">
                <div className="flex justify-between">
                  <span className="font-bold text-lg text-gray-900">Total:</span>
                  <span className="font-bold text-2xl text-green-600">${calculateTotal()} MXN</span>
                </div>
              </div>
            </div>
          </div>

          {/* Submit Status */}
          {submitStatus === 'success' && (
            <div className="bg-green-100 border-2 border-green-500 text-green-700 px-4 py-3 rounded-lg flex items-center gap-2">
              <i className="ri-check-line text-xl"></i>
              <span className="font-medium">¡Reserva enviada exitosamente! Te contactaremos pronto.</span>
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="bg-red-100 border-2 border-red-500 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
              <i className="ri-error-warning-line text-xl"></i>
              <span className="font-medium">Error al enviar la reserva. Por favor intenta nuevamente.</span>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium cursor-pointer whitespace-nowrap"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Enviando...' : 'Confirmar Reserva'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
